import { useEffect, useState } from "react";
import {
  setCookieConsent,
  getCookieConsent,
  type CookieConsent,
} from "@/lib/cookieConsent";

// Bottom-pinned banner shown until the user records a choice. Auth, admin,
// and pre-login surfaces all mount the same component — the banner doesn't
// know about routing because consent is global to the host.
//
// Reject-by-default: "Accept all" and "Reject non-essential" are equally
// prominent; "Customize" reveals per-category toggles.
//
// Mobile layout is intentionally compact: shorter copy, smaller padding,
// inline action row. Desktop expands to the fuller treatment.

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = getCookieConsent();
    if (!c) setOpen(true);
  }, []);

  function save(choice: Pick<CookieConsent, "analytics" | "marketing">) {
    setCookieConsent(choice);
    setOpen(false);
    setShowDetails(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pt-1 sm:px-6 sm:pb-6 sm:pt-2 pointer-events-none"
    >
      <div className="mx-auto max-w-[720px] bg-white border border-fh-border rounded-[12px] sm:rounded-[14px] shadow-card p-3.5 sm:p-6 pointer-events-auto">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            {/* Heading shown on desktop only; mobile leads with body copy to save vertical space. */}
            <h2
              id="cookie-banner-title"
              className="hidden sm:block font-serif text-[18px] text-fh-ink mb-1.5"
            >
              We use cookies
            </h2>
            {/* Mobile gets a short single-line summary; desktop gets the fuller paragraph. */}
            <p className="sm:hidden text-[12.5px] text-fh-gray leading-snug">
              We use cookies — necessary ones always; analytics & marketing with your okay.{" "}
              <a
                href="https://fortehealth.co/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fh-green hover:text-fh-green-dark font-semibold underline"
              >
                Privacy
              </a>
              .
            </p>
            <p className="hidden sm:block text-[13.5px] text-fh-gray leading-relaxed">
              We use strictly necessary cookies to run our patient portal and intake.
              With your permission, we also use analytics and marketing cookies to
              improve our care and reach more people who could benefit. You can
              change your choice anytime from your{" "}
              <a
                href="/account"
                className="text-fh-green hover:text-fh-green-dark font-semibold underline"
              >
                account settings
              </a>
              . See our{" "}
              <a
                href="https://fortehealth.co/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fh-green hover:text-fh-green-dark font-semibold underline"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>

          {showDetails && (
            <div className="border border-fh-border rounded-[10px] divide-y divide-fh-border">
              <Row
                title="Strictly necessary"
                description="Required for sign-in, security, and basic site function. Always on."
                checked
                disabled
              />
              <Row
                title="Analytics"
                description="Helps us understand which pages and features patients find useful. No PHI is shared."
                checked={analytics}
                onChange={setAnalytics}
              />
              <Row
                title="Marketing"
                description="Lets us measure ad performance so we can reach people seeking GLP-1 or TRT care."
                checked={marketing}
                onChange={setMarketing}
              />
            </div>
          )}

          {/* Compact action row on mobile (single line), spread on desktop. */}
          <div className="flex flex-row flex-wrap items-center gap-1.5 sm:gap-2.5 sm:justify-end">
            {!showDetails ? (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="text-[12px] sm:text-[13px] font-semibold text-fh-gray hover:text-fh-ink px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-[8px] sm:order-1"
              >
                Customize
              </button>
            ) : (
              <button
                type="button"
                onClick={() => save({ analytics, marketing })}
                className="text-[12px] sm:text-[13px] font-semibold text-fh-ink hover:text-fh-green-dark px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-[8px] border border-fh-border bg-white sm:order-1"
              >
                Save
              </button>
            )}
            <button
              type="button"
              onClick={() => save({ analytics: false, marketing: false })}
              className="text-[12px] sm:text-[13px] font-semibold text-fh-ink hover:text-fh-green-dark px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-[8px] border border-fh-border bg-white sm:order-3"
            >
              <span className="sm:hidden">Reject</span>
              <span className="hidden sm:inline">Reject non-essential</span>
            </button>
            <button
              type="button"
              onClick={() => save({ analytics: true, marketing: true })}
              className="text-[12px] sm:text-[13px] font-semibold text-white bg-fh-green-dark hover:bg-fh-green px-3 sm:px-4 py-1.5 sm:py-2 rounded-[8px] ml-auto sm:ml-0 sm:order-4"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 p-3 sm:p-3.5 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 w-4 h-4 accent-fh-green-dark disabled:opacity-60 disabled:cursor-not-allowed"
      />
      <div className="flex-1">
        <div className="text-[13px] sm:text-[13.5px] font-semibold text-fh-ink">{title}</div>
        <div className="text-[12px] sm:text-[12.5px] text-fh-gray leading-snug">{description}</div>
      </div>
    </label>
  );
}
